import { ChangeDetectorRef, Component,  Input, OnInit, ViewChild } from '@angular/core';
import { PageHandle, PAGE_NAME } from 'globals/store/permissions.data';
import { filterPageTypes, toggleFilterTypes, FilterType } from 'src/app/store/filters.era';
import { traceNames } from './../../services/performance.tracking.data';
import { readOnlyReselector } from 'src/app/store/reselectors/permissions.reselector';
import { taskTimeColumns, timeLogColumns, TimeLogData } from 'src/app/helpers/table-column-types';
import { TableColumn } from 'src/app/helpers/table.data';
import { taskTimeColumnsById, allDefaultTaskTimeColumns, allTaskTimeLogColumns, allTaskTimeLogColumnsById } from 'src/app/helpers/table-helpers';
import { loadLocal } from '../../helpers/helpers';
import { TaskTimeTableData } from './../../helpers/table-column-types';
import { LogEntry, Task, TimeLogSummary } from 'globals/store/tasks.data';
import { taskTimeTableReselector } from 'src/app/store/reselectors/tasks.reselector';
import { ManageTaskData} from '../manage/manage-task-data';
import { openDialogGetResult } from 'src/app/helpers/angular-helpers';
import { ManageTaskComponent } from '../manage/manage-task.component';
import { Wrapper } from 'src/app/components/wrapper.component';
import { saveTable } from '../performers/performerSharedFunctions';
import { NestedTableComponent } from 'src/app/components/nested-table/nested-table.component';
import { TimeLogDialogComponent, TimeLogDialogData } from '../../components/wizards/time-log-dialog.component';
import { LoaderPaneFragmentComponent } from 'src/app/components/loader/loader-pane-fragment.component';
import { TimeLogSummaryComponent } from '../manage/time-log-summary.component';
import { TasksCommandMenu } from 'src/app/components/home/tasks-command-menu.component';
import { JoiNavWrapper } from 'src/app/components/wrappers/joi-nav-wrapper.component';
import { TopMenuComponent } from '../program/top.menu';
import { MobileMenuComponent } from 'src/app/components/menu/mobile-menu.component';
import { tasksActions } from 'src/app/store/tasks.era';
import { CommonModule, DatePipe } from '@angular/common';

type TaskTimeSubTable = 'SUMMARY' | 'ITEMISED'

@Component({
    selector: 'task-time',
    template: `
<top-menu [showPrint]="true"></top-menu>
<mobile-menu></mobile-menu>
<joi-nav-wrapper 
  class="icon-tabs"
  [simple]="true"
  [menuItems]="subTables"
  [selectedId]="subTable" 
  (selectItem)="subTable = $event"
></joi-nav-wrapper>
<tasks-command-menu [canAddItem]="subTable === 'SUMMARY'" [filterPage]="filterPage" (saveTable)="saveTable()"></tasks-command-menu>
<time-log-summary 
  *ngIf="!!timeLogSummary?.hasEstimatedTime" 
  [showGreen]="true" 
  [timeLogSummary]="timeLogSummary"
></time-log-summary>
<nested-table *ngIf="subTable === 'SUMMARY'"
  [items]="taskTimeDataRows"
  [filtersId]="filterPage"
  [columns]="columnData"
  [columnsById]="columnsById"
  [condensedView]="condensed"
  [readOnly]="true"
  [defaultSelectedColumnId]="taskTimeColumns.name"
  emptyMessage="No tasks have logged or estimated times"
  (selectItem)="openTask($event)"
></nested-table>
<nested-table *ngIf="subTable === 'ITEMISED'"
  #timeLogTable
  emptyMessage="No tasks have logged or estimated times"
  [items]="timeLogData"
  [columns]="logColumns"
  [columnsById]="logColumnsById"
  [readOnly]="true"
  [defaultSelectedColumnId]="defaultColumnId"
  (selectItem)="openTimeLog($event)"
></nested-table>

<loader-pane-fragment [trace]="traceName"></loader-pane-fragment>
`,
    styleUrls: ['../../page.scss'],
    standalone: true,
    providers: [DatePipe],
    imports: [LoaderPaneFragmentComponent, CommonModule, NestedTableComponent, TimeLogSummaryComponent, TasksCommandMenu, JoiNavWrapper, TopMenuComponent, MobileMenuComponent],
})
export class TaskTimeComponent extends Wrapper<TaskTimeComponent> implements OnInit {

  @Input() isMyTasks = false
  @Input() page_name: PageHandle = PAGE_NAME.taskTime
  @Input() filterPage: FilterType = filterPageTypes.taskTime
  @Input() traceName = traceNames.viewTasksTime
  @ViewChild(NestedTableComponent) nestedTable: NestedTableComponent<TaskTimeTableData>
  @ViewChild('timeLogTable') timeLogTable: NestedTableComponent<TimeLogData>

  readOnly=true
  subTable: TaskTimeSubTable = 'SUMMARY'
  subTables: TaskTimeSubTable[] = ['SUMMARY', 'ITEMISED']
  
  timeLogSummary: TimeLogSummary
  taskTimeColumns = taskTimeColumns
  columnData: TableColumn[] = allDefaultTaskTimeColumns
  columnsById: { [id: string]: TableColumn } = taskTimeColumnsById
  condensed: boolean

  logColumns: TableColumn[] = allTaskTimeLogColumns
  logColumnsById = allTaskTimeLogColumnsById
  defaultColumnId = timeLogColumns.date
  

  taskTimeDataRows: TaskTimeTableData[]
  timeLogData: TimeLogData[]


  // taskTimeDataRowsById: { [id:string]: TaskTimeTableData }

  constructor (public changeDetectorRef: ChangeDetectorRef) {
    super()
    this.uiService.changePageTitle("Task Time Log", true)
  }

  ngOnInit() {
    this.subProp(this.store.select(readOnlyReselector), 'readOnly')
    this.loadCondensed()
    this.subCallback(this.store.select('filters', 'toggleFilters', this.filterPage, 'condensed'), _ => this.loadCondensed())
    this.subCallback(this.store.select(taskTimeTableReselector, this.filterPage), ({ summary, taskTimeDataRows, logs }: { summary: TimeLogSummary, taskTimeDataRows: TaskTimeTableData[], logs: TimeLogData[] }) => {
      this.timeLogSummary = summary
      this.taskTimeDataRows = taskTimeDataRows
      this.timeLogData = logs
    })

  }

  async saveTable() {
    const currentEvent = await this.eventService.getCurrentEvent();
    if (this.subTable === 'SUMMARY') {
      saveTable('time log' ,`${currentEvent.title} time log table`, this.dialog, this.nestedTable)
    } else {
      saveTable('itemised time log' ,`${currentEvent.title} itemised time log table`, this.dialog, this.timeLogTable)
    }
  }

  async openTimeLog(timeLogRow: TimeLogData) {
    if (this.readOnly) return

    const { fullTask: task, timeLog } = timeLogRow 
    const data: TimeLogDialogData = {
      notes: timeLog.content,
      time: timeLog.loggedTime,
      date: timeLog.createdAt.hasOwnProperty('seconds') ? new Date(timeLog.createdAt['seconds'] * 1000) : timeLog.createdAt,
      delete: true
    }
    const result = await openDialogGetResult<TimeLogDialogData>(this.dialog, TimeLogDialogComponent, { width: '300px', disableClose: true, data })
    if (!result) return
    if (result.delete) {
      const index = task.log.findIndex(l => l.id === timeLog.id)
      if (isNaN(index)) return
      task.log.splice(index, 1)
      this.saveTask(task)
      return
    } 
    const logEntry: LogEntry = {
      id: timeLog.id,
      content: result.notes,
      loggedTime: result.time,
      createdBy: this.userService.email,
      createdAt: result.date,
    }

    const index = task.log.findIndex(l => l.id === logEntry.id)
    task.log[index] = logEntry
    this.saveTask(task)
  }

  saveTask(task: Task) {
    this.taskService.updateTask(task.id, task)
  }

  loadCondensed() {
    this.condensed = loadLocal(`${this.filterPage}:${toggleFilterTypes.condensed}`, false)
  } 

  async openTask({ task }: TaskTimeTableData) {
    // console.log("Should be opening...", task)
    const data: ManageTaskData = { task: task, readOnly: this.readOnly }
    this.store.dispatch(tasksActions.taskInFocus(task.id))
    this.changeDetectorRef.detach()
    await openDialogGetResult(this.dialog, ManageTaskComponent, { autoFocus: false, disableClose: true, data, panelClass: 'full-screen-modal' })
    this.changeDetectorRef.reattach()
    this.store.dispatch(tasksActions.taskInFocus(null))
    // if (result === 'Delete') this.dontSortTemporarily() <== would need to implement for nested table?
  }

}




